import { Box, BoxProps } from '@mui/material'
import { forwardRef, useEffect, useMemo, useState } from 'react'
import { Fade } from 'utoniq-ui'

import { styles, styleSets } from '@/components/styles'
import { OrganismFrame, OrganismFrameProps } from '@/core/architecture'
import { useImage } from '@/lib/image'

export type HeaderImageAreaProps = OrganismFrameProps & {
  imageUri?: string | null
  hasSafeArea?: boolean
  bgImage?: string | null
  gradationMask?: boolean
  responsive?: boolean
  maxWidth?: number
  hasHeightLimit?: boolean
  imageProps?: BoxProps
}

export const HeaderImageArea = forwardRef<HTMLDivElement, HeaderImageAreaProps>(
  (
    {
      imageUri,
      hasSafeArea,
      bgImage,
      gradationMask,
      children,
      responsive = false,
      maxWidth,
      imageProps,
      ...props
    },
    ref
  ) => {
    const image = useImage(imageUri || '')
    const bannerAspectRatio =
      !!image?.naturalWidth && !!image?.naturalHeight
        ? image.naturalHeight / image.naturalWidth
        : null

    const [windowWidth, setWindowWidth] = useState(
      typeof window !== 'undefined' ? window.innerWidth : 0
    )

    useEffect(() => {
      if (!responsive) return () => {}
      const handleResize = () => setWindowWidth(window.innerWidth)
      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }, [])

    const displayWidth = useMemo(() => {
      if (!bannerAspectRatio || !maxWidth || !responsive) return null
      const widthByMaxWidth = maxWidth ?? windowWidth
      const widthByHeightLimit =
        bannerAspectRatio > 0 ? 768 / bannerAspectRatio : widthByMaxWidth
      return Math.min(windowWidth, widthByMaxWidth, widthByHeightLimit)
    }, [bannerAspectRatio, maxWidth, windowWidth, responsive])

    return (
      <OrganismFrame
        {...props}
        sx={{
          height: styleSets.bgImageHeight(
            bannerAspectRatio,
            maxWidth,
            responsive
          ),
          maxHeight: 768,
          pt: hasSafeArea ? styles.safeAreaTop : 0,
          position: 'relative',
          zIndex: 0,
          overflow: 'hidden',
          ...props?.sx,
        }}
      >
        {!!imageUri && (
          <Fade in>
            <Box
              ref={ref}
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                zIndex: -1,
                backgroundImage: `url(${imageUri})`,
                backgroundSize: responsive ? 'contain' : 'cover',
                backgroundRepeat: responsive ? 'no-repeat' : undefined,
                backgroundPosition: 'center',
                visibility:
                  !!image?.naturalWidth && !!image?.naturalHeight
                    ? 'visible'
                    : 'hidden',
                ...imageProps?.sx,
              }}
            />
          </Fade>
        )}
        {gradationMask && (
          <Box
            ref={ref}
            sx={{
              pointerEvents: 'none',
              bgcolor: 'background.default',
              // レスポンシブの時は画像が画面外にはみ出るケースがあるのでマスクの高さはvhではなく%で計算する
              // レスポンシブ非適用のサイトでは画像がはみ出ることがほぼあり得ないためvhでも問題なし、かつ%使用時の挙動が未確認のため
              // レスポンシブ適用時のみを対象とする
              height: responsive ? '100%' : '100vh',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'absolute',
              zIndex: 1,
              top: 0,
              width: '100%',
              // レスポンシブの時は画像が画面外にはみ出るケースがあるのでマスクの高さはvhではなく%で計算する
              // レスポンシブ非適用のサイトでは画像がはみ出ることがほぼあり得ないためvhでも問題なし、かつ%使用時の挙動が未確認のため
              // レスポンシブ適用時のみを対象とする
              maskImage: () => {
                const gradationStyle = (width: string) =>
                  `linear-gradient(0deg, black calc(${
                    responsive ? '100%' : '100vh'
                  } - ${width} * ${
                    bannerAspectRatio ?? 0
                  } + 24px), transparent calc(${
                    responsive ? '100%' : '100vh'
                  } - ${width} * ${bannerAspectRatio ?? 0} + 140px))`

                // レスポンシブの時は画像の大きさ・アスペクト比に関わらず常に一定のグラデーション幅になるよう画像横幅から計算する
                return displayWidth
                  ? gradationStyle(`${displayWidth}px`)
                  : {
                      xs: gradationStyle('100vw'),
                      sm: gradationStyle('600px'),
                    }
              },
              backgroundImage: bgImage ? `url(${bgImage})` : 'none',
            }}
          />
        )}
        {children}
      </OrganismFrame>
    )
  }
)
